/**
 * Configuración del Router
 * Sprint 3: Inicialización de device identity
 * Sprint 5: Rutas de verificación OTP
 * Sprint 7: Rutas de admin panel + TrackingProvider global
 *
 * Optimización: Lazy loading de páginas con React.lazy + Suspense.
 * Solo Login y Register se cargan eagerly (flujo de entrada).
 */

import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { MARKETPLACE_MODE_ONBOARDED_KEY, useAuthStore, useMarketplaceStore, useProviderContextStore } from '../stores';
import { PushProvider } from '../providers';
import { ProtectedRoute, GuestRoute, AdminRoute, RouteErrorBoundary } from '../components/routing';
import { AdminLayout } from '../components/admin';
import { TrackingProvider } from '../components/tracking';
import { AppLayout } from '../components/layout';

// Eager: páginas del flujo de entrada (siempre necesarias)
import { LoginPage } from '../pages/Login/Login';
import { RegisterPage } from '../pages/Register/Register';

// Lazy: páginas que se cargan bajo demanda
const HomePage = lazy(() => import('../pages/Home/Home').then(m => ({ default: m.HomePage })));
const VerifyAccountPage = lazy(() => import('../pages/VerifyAccount/VerifyAccount').then(m => ({ default: m.VerifyAccountPage })));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPassword/ForgotPassword').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../pages/ResetPassword/ResetPassword').then(m => ({ default: m.ResetPasswordPage })));
const DeviceUnbindPage = lazy(() => import('../pages/DeviceUnbind/DeviceUnbind').then(m => ({ default: m.DeviceUnbindPage })));
const SettingsPage = lazy(() => import('../pages/Settings/Settings').then(m => ({ default: m.SettingsPage })));
const ClientFiscalSettingsPage = lazy(() => import('../pages/Settings/ClientFiscalSettings').then(m => ({ default: m.ClientFiscalSettingsPage })));
const ClientLegalDocumentsSettingsPage = lazy(() => import('../pages/Settings/ClientLegalDocumentsSettings').then(m => ({ default: m.ClientLegalDocumentsSettingsPage })));
const AccountDeactivationSettingsPage = lazy(() => import('../pages/Settings/AccountDeactivationSettings').then(m => ({ default: m.AccountDeactivationSettingsPage })));
const UserProfilePage = lazy(() => import('../pages/UserProfile/UserProfilePage').then(m => ({ default: m.UserProfilePage })));
const ModeSelectionPage = lazy(() => import('../pages/marketplace/ModeSelectionPage').then(m => ({ default: m.ModeSelectionPage })));

// Lazy: Chat
const ChatConversationsPage = lazy(() => import('../pages/chat/ChatConversations').then(m => ({ default: m.ChatConversationsPage })));
const ChatConversationPage = lazy(() => import('../pages/chat/ChatConversation').then(m => ({ default: m.ChatConversationPage })));

// Lazy: Troquero
const RequestFormPage = lazy(() => import('../pages/marketplace/RequestFormPage').then(m => ({ default: m.RequestFormPage })));
const AddressAdjustmentPage = lazy(() => import('../pages/marketplace/AddressAdjustmentPage').then(m => ({ default: m.AddressAdjustmentPage })));
const MyRequestsPage = lazy(() => import('../pages/marketplace/MyRequestsPage').then(m => ({ default: m.MyRequestsPage })));
const RequestDetailPage = lazy(() => import('../pages/marketplace/RequestDetailPage').then(m => ({ default: m.RequestDetailPage })));
const RequestQuotesPage = lazy(() => import('../pages/marketplace/RequestQuotesPage').then(m => ({ default: m.RequestQuotesPage })));
const RequestPointDetailPage = lazy(() => import('../pages/marketplace/RequestPointDetailPage').then(m => ({ default: m.RequestPointDetailPage })));
const OperatorOnboardingPage = lazy(() => import('../pages/marketplace/OperatorOnboardingPage').then(m => ({ default: m.OperatorOnboardingPage })));
const OperatorFeedPage = lazy(() => import('../pages/marketplace/OperatorFeedPage').then(m => ({ default: m.OperatorFeedPage })));
const ContractsPage = lazy(() => import('../pages/marketplace/ContractsPage').then(m => ({ default: m.ContractsPage })));
const ContractDetailPage = lazy(() => import('../pages/marketplace/ContractDetailPage').then(m => ({ default: m.ContractDetailPage })));
const TrackingPage = lazy(() => import('../pages/marketplace/TrackingPage').then(m => ({ default: m.TrackingPage })));
const InvitationDecisionPage = lazy(() => import('../pages/marketplace/InvitationDecisionPage').then(m => ({ default: m.InvitationDecisionPage })));
const OperatorPayoutsPage = lazy(() => import('../pages/operator/OperatorPayouts').then(m => ({ default: m.OperatorPayoutsPage })));
const ProviderContextPage = lazy(() => import('../pages/providers/ProviderContextPage').then(m => ({ default: m.ProviderContextPage })));
const ProviderOnboardingPage = lazy(() => import('../pages/providers/ProviderOnboardingPage').then(m => ({ default: m.ProviderOnboardingPage })));
const ProviderInvitationAcceptPage = lazy(() => import('../pages/providers/ProviderInvitationAcceptPage').then(m => ({ default: m.ProviderInvitationAcceptPage })));
const ProviderPanelPage = lazy(() => import('../pages/providers/ProviderPanelPage').then(m => ({ default: m.ProviderPanelPage })));
const DriverShiftPage = lazy(() => import('../pages/driver/DriverShiftPage').then(m => ({ default: m.DriverShiftPage })));

// Lazy: Carta Porte
const CartaPorteDetailPage = lazy(() => import('../pages/CartaPorte/CartaPorteDetailPage').then(m => ({ default: m.CartaPorteDetailPage })));
const CartaPorteListPage = lazy(() => import('../pages/CartaPorte/CartaPorteListPage').then(m => ({ default: m.CartaPorteListPage })));
const CartaPorteWidgetPage = lazy(() => import('../pages/CartaPorte/CartaPorteWidgetPage').then(m => ({ default: m.CartaPorteWidgetPage })));

// Lazy: Admin
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminPayments = lazy(() => import('../pages/admin/AdminPayments').then(m => ({ default: m.AdminPayments })));
const AdminReportedRatings = lazy(() => import('../pages/admin/AdminReportedRatings').then(m => ({ default: m.AdminReportedRatings })));
const AdminTracking = lazy(() => import('../pages/admin/AdminTracking').then(m => ({ default: m.AdminTracking })));
const AdminPushMonitor = lazy(() => import('../pages/admin/AdminPushMonitor').then(m => ({ default: m.AdminPushMonitor })));
const AdminReturnIntents = lazy(() => import('../pages/admin/AdminReturnIntents').then(m => ({ default: m.AdminReturnIntents })));
const AdminUserList = lazy(() => import('../pages/admin/AdminUserList').then(m => ({ default: m.AdminUserList })));
const AdminUserDetail = lazy(() => import('../pages/admin/AdminUserDetail').then(m => ({ default: m.AdminUserDetail })));
const AdminMap = lazy(() => import('../pages/admin/AdminMap').then(m => ({ default: m.AdminMap })));
const CreateAdmin = lazy(() => import('../pages/admin/CreateAdmin').then(m => ({ default: m.CreateAdmin })));
const AdminPayoutSettings = lazy(() => import('../pages/admin/AdminPayoutSettings').then(m => ({ default: m.AdminPayoutSettings })));
const AdminSystemSettings = lazy(() => import('../pages/admin/AdminSystemSettings').then(m => ({ default: m.AdminSystemSettings })));
const AdminLegalDocumentsSettings = lazy(() => import('../pages/admin/AdminLegalDocumentsSettings').then(m => ({ default: m.AdminLegalDocumentsSettings })));
const AdminBillingSettings = lazy(() => import('../pages/admin/billing/AdminBillingSettings').then(m => ({ default: m.AdminBillingSettings })));
const AdminBillingInvoices = lazy(() => import('../pages/admin/billing/AdminBillingInvoices').then(m => ({ default: m.AdminBillingInvoices })));
const AdminCargoInsurancePage = lazy(() => import('../pages/admin/cargoInsurance/AdminCargoInsurancePage').then(m => ({ default: m.AdminCargoInsurancePage })));

import { initializeDeviceIdentity } from '../services/deviceIdentity';
import { initializeFileDownloadAccess } from '../services/fileDownload';
import { Spinner } from '../components/ui';

function LazyFallback() {
    return (
        <div className="flex min-h-[50dvh] items-center justify-center">
            <Spinner size="large" />
        </div>
    );
}

function normalizePath(pathname: string): string {
    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname.slice(0, -1);
    }

    return pathname;
}

function isMainRoute(pathname: string): boolean {
    return pathname === '/' || pathname === '/admin';
}

function getMainRouteForPath(pathname: string): string {
    return pathname.startsWith('/admin') ? '/admin' : '/';
}

function AndroidBackButtonHandler() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathnameRef = useRef(location.pathname);

    useEffect(() => {
        pathnameRef.current = location.pathname;
    }, [location.pathname]);

    useEffect(() => {
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
            return;
        }

        let listenerHandle: PluginListenerHandle | undefined;

        const registerBackButtonListener = async () => {
            listenerHandle = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
                const currentPath = normalizePath(pathnameRef.current);

                // En pantallas principales no hacemos nada y evitamos cerrar la app.
                if (isMainRoute(currentPath)) {
                    return;
                }

                if (canGoBack) {
                    window.history.back();
                    return;
                }

                navigate(getMainRouteForPath(currentPath), { replace: true });
            });
        };

        void registerBackButtonListener();

        return () => {
            if (!listenerHandle) {
                return;
            }

            void listenerHandle.remove();
        };
    }, [navigate]);

    return null;
}

interface ModeOnboardingRouteProps {
    children: React.ReactNode;
}

function ModeOnboardingRoute({ children }: ModeOnboardingRouteProps) {
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const { modeLoaded, isLoadingMode, fetchMode } = useMarketplaceStore();
    const operationMode = useMarketplaceStore((state) => state.operationMode);
    const providerContexts = useProviderContextStore((state) => state.contexts);
    const providerContextsLoaded = useProviderContextStore((state) => state.contextsLoaded);
    const providerContextError = useProviderContextStore((state) => state.error);
    const requiresProviderSelection = useProviderContextStore((state) => state.requiresSelection);
    const providerLoading = useProviderContextStore((state) => state.isLoading);
    const isModeOnboarded = localStorage.getItem(MARKETPLACE_MODE_ONBOARDED_KEY) === '1';

    useEffect(() => {
        if (!modeLoaded && user && !user.is_admin) {
            void fetchMode();
        }
    }, [modeLoaded, user, fetchMode]);

    if (user && !user.is_admin && !modeLoaded && isLoadingMode) {
        return (
            <div className="route-loading">
                <Spinner size="large" />
            </div>
        );
    }

    if (user && !user.is_admin && modeLoaded && !isModeOnboarded) {
        return <Navigate to="/mode/select" replace />;
    }

    if (
        user
        && !user.is_admin
        && modeLoaded
        && (operationMode ?? user.operation_mode) === 'operator'
        && providerContextsLoaded
        && providerContexts.length === 0
        && providerContextError === null
        && !providerLoading
        && location.pathname !== '/providers/onboarding'
    ) {
        return <Navigate to="/providers/onboarding" replace />;
    }

    if (
        user
        && !user.is_admin
        && modeLoaded
        && (operationMode ?? user.operation_mode) === 'operator'
        && requiresProviderSelection
        && !providerLoading
        && location.pathname !== '/providers/context'
    ) {
        return <Navigate to="/providers/context" replace />;
    }

    return <>{children}</>;
}

export function AppRouter() {
    const { checkAuth, isInitialized } = useAuthStore();
    const [deviceReady, setDeviceReady] = useState(false);

    // Inicializar device identity al montar la app
    useEffect(() => {
        const initDevice = async () => {
            try {
                await initializeDeviceIdentity();
                await initializeFileDownloadAccess();
                setDeviceReady(true);
            } catch (error) {
                console.error('Error initializing device identity:', error);
                // Continuar de todos modos - el login manejará el error
                setDeviceReady(true);
            }
        };

        initDevice();
    }, []);

    // Verificar autenticación al montar la app
    useEffect(() => {
        if (!isInitialized && deviceReady) {
            checkAuth();
        }
    }, [checkAuth, isInitialized, deviceReady]);

    // Mostrar spinner mientras se inicializa el dispositivo
    if (!deviceReady) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-background">
                <Spinner size="large" />
            </div>
        );
    }


    return (
        <BrowserRouter>
            <AndroidBackButtonHandler />
            <PushProvider>
                <Suspense fallback={<LazyFallback />}>
                <Routes>
                    {/* Rutas públicas (solo para invitados) */}
                    <Route
                        path="/login"
                        element={
                            <GuestRoute>
                                <LoginPage />
                            </GuestRoute>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <GuestRoute>
                                <RegisterPage />
                            </GuestRoute>
                        }
                    />

                    {/* Sprint 5: Rutas de verificación OTP (públicas) */}
                    <Route path="/verify-account" element={<VerifyAccountPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/device-unbind" element={<DeviceUnbindPage />} />

                    {/* Rutas protegidas (requieren autenticación) */}
                    {/* AppLayout envuelve las pantallas de usuario para proporcionar
                        la barra de navegación inferior persistente y layout consistente.
                        TrackingProvider envuelve las rutas que necesitan tracking GPS. */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <TrackingProvider>
                                        <AppLayout>
                                            <HomePage />
                                        </AppLayout>
                                    </TrackingProvider>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/mode/select"
                        element={
                            <ProtectedRoute>
                                <ModeSelectionPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Sprint 8: Configuración (para todos los usuarios autenticados) */}
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <SettingsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/client/fiscal"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <ClientFiscalSettingsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/client/legal-documents"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <ClientLegalDocumentsSettingsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/account/deactivation"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <AccountDeactivationSettingsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/:userId"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <UserProfilePage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />

                    {/* Sprint 11: Chat */}
                    <Route
                        path="/chat"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <TrackingProvider>
                                        <AppLayout>
                                            <ChatConversationsPage />
                                        </AppLayout>
                                    </TrackingProvider>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/chat/conversations/:conversationId"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <TrackingProvider>
                                        <AppLayout>
                                            <ChatConversationPage />
                                        </AppLayout>
                                    </TrackingProvider>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />

                    {/* Sprint 12: Troquero */}
                    <Route
                        path="/marketplace/requests"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <MyRequestsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/new"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestFormPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/:requestId/edit"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestFormPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/address-adjustment"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <AddressAdjustmentPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/:requestId/quote"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestDetailPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/:requestId/quotes"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestQuotesPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/:requestId"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestDetailPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/requests/:requestId/points/:pointId"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <RequestPointDetailPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/feed"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <OperatorFeedPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/operator/onboarding"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="profile-settings-content">
                                        <RouteErrorBoundary routeName="Perfil del Operador">
                                            <OperatorOnboardingPage />
                                        </RouteErrorBoundary>
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/contracts"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <ContractsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/contracts/:contractId"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <ContractDetailPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/contracts/:contractId/tracking"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <TrackingProvider>
                                        <AppLayout>
                                            <TrackingPage />
                                        </AppLayout>
                                    </TrackingProvider>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/operator/payouts"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <OperatorPayoutsPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/operator/provider-payouts"
                        element={<Navigate to="/operator/payouts" replace />}
                    />
                    <Route
                        path="/providers/invitations/:token"
                        element={<ProviderInvitationAcceptPage />}
                    />
                    <Route
                        path="/providers/onboarding"
                        element={
                            <ProtectedRoute>
                                <ProviderOnboardingPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/providers/context"
                        element={
                            <ProtectedRoute>
                                <ProviderContextPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/providers/panel"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <ProviderPanelPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/driver/shift"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <TrackingProvider>
                                        <AppLayout>
                                            <DriverShiftPage />
                                        </AppLayout>
                                    </TrackingProvider>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace/invitations/:token"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <InvitationDecisionPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />

                    {/* Sprint D/E: Rutas de Carta Porte */}
                    <Route
                        path="/carta-porte"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <CartaPorteListPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/carta-porte/config"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <Navigate to="/marketplace/operator/onboarding?section=fiscal" replace />
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/carta-porte/:id/widget"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout className="max-w-none">
                                        <CartaPorteWidgetPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/carta-porte/:id"
                        element={
                            <ProtectedRoute>
                                <ModeOnboardingRoute>
                                    <AppLayout>
                                        <CartaPorteDetailPage />
                                    </AppLayout>
                                </ModeOnboardingRoute>
                            </ProtectedRoute>
                        }
                    />

                    {/* Sprint 7: Rutas de admin panel */}
                    <Route
                        path="/admin"
                        element={
                            <AdminRoute>
                                <TrackingProvider>
                                    <AdminLayout />
                                </TrackingProvider>
                            </AdminRoute>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="payments" element={<AdminPayments />} />
                        <Route path="reported-ratings" element={<AdminReportedRatings />} />
                        <Route path="tracking" element={<AdminTracking />} />
                        <Route path="return-intents" element={<AdminReturnIntents />} />
                        <Route path="push-monitor" element={<AdminPushMonitor />} />
                        <Route path="payouts/settings" element={<AdminPayoutSettings />} />
                        <Route path="billing/settings" element={<AdminBillingSettings />} />
                        <Route path="billing/invoices" element={<AdminBillingInvoices />} />
                        <Route path="cargo-insurance" element={<AdminCargoInsurancePage />} />
                        <Route path="users" element={<AdminUserList />} />
                        <Route path="users/create" element={<CreateAdmin />} />
                        <Route path="users/:id" element={<AdminUserDetail />} />
                        <Route path="map" element={<AdminMap />} />
                        <Route path="system-settings" element={<AdminSystemSettings />} />
                        <Route path="legal-documents" element={<AdminLegalDocumentsSettings />} />
                    </Route>

                    {/* Fallback: redirigir a home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
            </PushProvider>
        </BrowserRouter>
    );
}
